import { useCallback, useState, type ReactNode } from "react";
import { t } from "../i18n";

// Several shares or recordings picked to delete at once (byj-1wv.43): a box
// on each card that may go, a box that picks them all, and a button that
// deletes what is picked after one question.

export function useSelection() {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const toggle = useCallback((id: string) => {
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);
  const setAll = useCallback((ids: string[], on: boolean) => setPicked(on ? new Set(ids) : new Set()), []);
  /** Forget what is no longer on the list (deleted, or gone on a reload). */
  const keepOnly = useCallback((ids: string[]) => {
    setPicked((p) => new Set([...p].filter((id) => ids.includes(id))));
  }, []);
  return { picked, toggle, setAll, keepOnly };
}

/** The heading line's bar: select all, and delete what is selected (where `onDelete` is given). */
export function SelectionBar({
  ids,
  picked,
  setAll,
  onDelete,
  more,
}: {
  ids: string[];
  picked: Set<string>;
  setAll: (ids: string[], on: boolean) => void;
  onDelete?: () => void;
  /** What else is done with what is picked, beside Delete: a review request (byj-1wv.44), an assignment set to all (byj-1wv.51.6). */
  more?: (n: number) => ReactNode;
}) {
  if (ids.length === 0) return null;
  const n = ids.filter((id) => picked.has(id)).length;
  const all = n === ids.length;
  return (
    <div className="selection-bar row">
      <label className="toggle small select-all">
        <input
          type="checkbox"
          checked={all}
          ref={(el) => {
            if (el) el.indeterminate = n > 0 && !all;
          }}
          onChange={(e) => setAll(ids, e.target.checked)}
        />{" "}
        {t("recordings:selectAll")}
      </label>
      {n > 0 && more?.(n)}
      {n > 0 && onDelete && (
        <button className="small danger" onClick={onDelete}>
          {t("recordings:deleteSelected", { n })}
        </button>
      )}
    </div>
  );
}

/**
 * A list picked from only once its Select is pressed (/shares): the picks, and
 * whether the boxes are out. Done puts them away and forgets the picks.
 */
export function useSelectMode() {
  const selection = useSelection();
  const [selecting, setSelecting] = useState(false);
  const { setAll } = selection;
  // kept stable, so what listens for it (an Esc) is not set up again each render
  const select = useCallback(
    (on: boolean) => {
      setSelecting(on);
      if (!on) setAll([], false);
    },
    [setAll],
  );
  return { ...selection, selecting, select };
}

/** Select, or while selecting the bar (select all, delete, `more`) and Done: nothing when no row may be picked. */
export function SelectControls({
  ids,
  mode,
  onDelete,
  more,
}: {
  ids: string[];
  mode: ReturnType<typeof useSelectMode>;
  onDelete?: () => void;
  more?: (n: number) => ReactNode;
}) {
  if (ids.length === 0) return null;
  return (
    <span className="row-actions">
      {mode.selecting && <SelectionBar ids={ids} picked={mode.picked} setAll={mode.setAll} onDelete={onDelete} more={more} />}
      <button type="button" className="small" aria-pressed={mode.selecting} onClick={() => mode.select(!mode.selecting)}>
        {mode.selecting ? t("shares:selectDone") : t("shares:selectMode")}
      </button>
    </span>
  );
}

/**
 * One change made to each of several picked, one call after another as
 * /shares deletes several (byj-1wv.43), none stopping the rest: how many were
 * done, what refused the others, to be said back, and the ones refused —
 * left picked, as /shares leaves what it could not delete, to try again
 * (byj-1wv.51.13).
 */
export async function eachPicked<T>(items: T[], f: (x: T) => Promise<unknown>): Promise<{ done: number; refused: string[]; left: T[] }> {
  let done = 0;
  const refused: string[] = [];
  const left: T[] = [];
  for (const x of items) {
    try {
      await f(x);
      done++;
    } catch (err) {
      refused.push(err instanceof Error ? err.message : String(err));
      left.push(x);
    }
  }
  return { done, refused, left };
}

/**
 * What every one of several picked holds alike, or null where they differ:
 * a change to them starts from it, and a value they differ in is Mixed,
 * each keeping its own until it is changed (byj-1wv.51.13).
 */
export function alike<T>(items: T[], of: (x: T) => string): string | null {
  const vs = new Set(items.map(of));
  return vs.size === 1 ? [...vs][0]! : null;
}

/** A card's own box. */
export function SelectBox({ id, title, picked, toggle }: { id: string; title: string; picked: Set<string>; toggle: (id: string) => void }) {
  return (
    <input
      type="checkbox"
      className="select-box"
      aria-label={t("recordings:select", { title })}
      checked={picked.has(id)}
      onChange={() => toggle(id)}
    />
  );
}
